import { Form, Formik, useFormikContext } from "formik";
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import axios from "axios";
import { flatten } from "underscore";

import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Button from "@material-ui/core/Button";

import Grid from "@material-ui/core/Grid";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import CreateIcon from "@material-ui/icons/Create";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";

import InputOption from "./Input";
import RadioOption from "./Radio";
import NumberOption from "./Number";

import { SizeManager } from "../DeskpadOptions";
import { useCanvasMetrics } from "utils/canvas";
import t from "translations";

//TODO: rework first step size compoennt
const optionTypes = {
  input: ["width", "height", "amount"],
  radio: ["radio"],
  number: ["number", "cross_sell_pc"],
};

const getOptionType = (type_code) => {
  const entity = Object.entries(optionTypes).find((entity) =>
    entity[1].includes(type_code)
  );
  return entity?.length && entity[0];
};

const OptionChildrenRenderer = (props) => {
  const { option } = props;
  const type = getOptionType(option.type_code);
  switch (type) {
    case "input":
      return <InputOption {...props} />;
    case "radio":
      return <RadioOption {...props} />;
    case "number":
      return <NumberOption {...props} />;
    default:
      return (
        <div>{`Invalid option type ${type}, based on ${option.type_code}`}</div>
      );
  }
};

const OptionContentWrap = styled.div({
  display: "flex",
  flexWrap: "wrap",
  width: "100%",
});

const OptionContent = ({ options }) => {
  const children = options?.reduce(
    (children, opt) => [...children, ...opt.children],
    []
  );

  return (
    <OptionContentWrap>
      {children.map((child) => {
        return (
          <OptionChildrenRenderer
            key={child.code}
            name={options[0].code}
            option={child}
          />
        );
      })}
    </OptionContentWrap>
  );
};

const AccordionTitle = styled.div({
  alignItems: "center",
  display: "flex",
  ">span": {
    backgroundColor: "rgb(236,243,214)",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    display: "flex",
    justifyContent: "center",
    color: "#A2C536",
    alignItems: "center",
  },
});

const OptionHeader = ({ num, name, value, onEditClicked, editable }) => {
  return (
    <AccordionSummary
      onClick={() => onEditClicked?.(num)}
      style={{ opacity: editable ? 1 : 0.5 }}
    >
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <AccordionTitle>
          <span>{num + 1}</span>
          <Typography style={{ paddingLeft: 10 }}>
            <b>{name}</b> {value}
          </Typography>
        </AccordionTitle>
        {editable && (
          <ListItemIcon style={{ minWidth: "unset" }}>
            <CreateIcon />
          </ListItemIcon>
        )}
      </Grid>
    </AccordionSummary>
  );
};

const typesWithNextButton = ["number", "cross_sell_pc"];

const Option = ({
  options,
  num,
  expanded,
  onEditClicked,
  onNextStep,
  value,
  name,
  disabled = false,
}) => {
  const optionName =
    name ??
    options?.reduce((name, opt) => `${name}, ${opt.name}`, "")?.substring(1);
  const hasNextButton = !!options
    .flatMap((opt) => opt?.children)
    .map((opt) => opt?.type_code)
    .map((code) => typesWithNextButton?.includes(code))
    .filter(Boolean).length || num === 0;

  return (
    <Accordion expanded={expanded === num}>
      <OptionHeader
        editable={!disabled}
        num={num}
        name={optionName}
        value={value}
        onEditClicked={onEditClicked}
      />
      {!disabled && (
        <AccordionDetails>
          <OptionContent options={options} />
        </AccordionDetails>
      )}
      {hasNextButton && (
        <Button variant="outlined" fullWidth onClick={onNextStep}>
          Next
        </Button>
      )}
    </Accordion>
  );
};

const arrayIncludesAllValuesFromArray = (arrayWithAllValues, array) => {
  let includes = true;
  array.forEach((el) => {
    if (!arrayWithAllValues.includes(el)) {
      includes = false;
      return;
    }
  });
  return includes;
};

const Options = ({
  productCode,
  onOptionsChanged,
  onCodesChanged,
  loadedOptions,
  isLoadedProduct,
}) => {
  const [expanded, setExpanded] = useState(-1);
  const [rawOptionsList, setRawOptionsList] = useState([]);

  const [currentStep, setCurrentStep] = useState([]);
  const [designStep, setDesignStep] = useState(-1);

  const [isLoading, setIsLoading] = useState(false);
  const {
    values,
    touched,
    errors,
    setFieldValue,
    resetForm,
    setFieldTouched,
    ...formikProps
  } = useFormikContext();

  window.formikProps = formikProps;

  const optionsList = Object.entries(values)
    .map(([code, value]) =>
      Number.isInteger(value) ? { code, value } : { code: value }
    )
    .filter(({ code }) => !!code);

  const loadStep = async () => {
    console.log("Loading with option codes", optionsList);
    setIsLoading(true);
    try {
      const resp = await axios.post("/wp-json/dpe/v1/probo/product-options", {
        products: [
          {
            code: productCode,
            options: optionsList,
          },
        ],
      });
      const respProduct = resp.data.products[0];
      const options = respProduct["available_options"];
      const selected = respProduct["selected_options"];
      // const pricesTotal = respProduct["prices_total"];
      const canOrder = respProduct["can_order"];
      const uploader = respProduct.uploaders;

      if (selected.length) {
        onOptionsChanged(selected);
      }
      if (options.length && options[0].code === "uploader-application") {
        setFieldValue("designtype", "customer-supplied-file");
        setFieldTouched("designtype", true);
        setDesignStep(expanded);
        return;
      }
      //NOTE: options is already in list
      // e.g. Fototapete last step with no options selected
      const alreadyLoaded = !!rawOptionsList?.find(
        (opt) => opt[0]?.code === options[0]?.code
      );
      if (canOrder) {
        onCodesChanged(optionsList, uploader[0]);
        if (alreadyLoaded || !options.length) {
          setExpanded(expanded + 1);
          setIsLoading(false);
          console.log("It's an end");
          return;
        }
      }

      setRawOptionsList([...rawOptionsList, options]);
      setCurrentStep(options);
      setExpanded(expanded + 1);
      console.log("Loaded new options", options, "response:", resp.data);
    } catch (err) {
      console.error("Options failed", err.message, err.response);
    }
    setIsLoading(false);
  };

  const firstStep = currentStep?.find((opt) => opt.code === "amount");

  /* NOTE: on a first step we can have size[width, height] and amount options
   * here we are getting inside size option codes (width, height)
   * to make sure they are all touched and we can go next
   */
  const currentStepCodes = firstStep
    ? currentStep
        ?.reduce((children, opt) => [...children, ...opt.children], [])
        .map((child) => child.code)
    : currentStep?.map((opt) => opt.code);

  useEffect(() => {
    console.group("Touched");
    console.log(touched);
    const touchedValues = Object.keys(touched).filter((key) => touched[key]);
    console.log("Current step codes", currentStepCodes);
    console.log("Touched codes", touchedValues);
    const stepFilled = arrayIncludesAllValuesFromArray(
      touchedValues,
      flatten(currentStepCodes)
    );
    console.log("Step filled", stepFilled);
    if (stepFilled && Object.keys(touched)) {
      if (rawOptionsList.length) {
        loadStep();
      }
    }
    console.groupEnd();
  }, [touched]);

  useEffect(() => {
    console.log("Probo form errors", errors);
  }, [errors]);

  const reset = () => {
    setCurrentStep([]);
    setRawOptionsList([]);
    setIsLoading(false);
    setExpanded(-1);
    setDesignStep(-1);
    onCodesChanged([]);
    onOptionsChanged([]);
    resetForm();
  };

  useEffect(() => {
    reset();
  }, [productCode]);

  useEffect(() => {
    console.log("VALUES", values);
  }, [values]);

  useEffect(() => {
    if (!rawOptionsList?.length && !isLoading) {
      loadStep();
    }
  }, [rawOptionsList]);

  const disabledStyles = {
    opacity: 0.5,
    pointerEvents: "none",
  };

  const isTouched = !!Object.keys(touched).length;
  const productWithOptionsLoaded =
        Boolean(isLoadedProduct && !Object.keys(values)?.length && loadedOptions?.length);
  return (
    <>
      {(isTouched || productWithOptionsLoaded) && (
        <Button
          variant="outlined"
          fullWidth
          style={{ marginBottom: 10 }}
          onClick={reset}
        >
          <DeleteForeverIcon />
          Zurücksetzten
        </Button>
      )}
      {(isLoading || !rawOptionsList.length) && <LinearProgress />}
      {productWithOptionsLoaded && (
        <LoadedOptions loadedOptions={loadedOptions} />
      )}
      {!productWithOptionsLoaded && (
        <Form style={isLoading ? disabledStyles : {}}>
          {rawOptionsList.map((options, index) => {
            const optionValue = values[options[0]?.code];
            let name = options[0]?.children?.find(
              (opt) => opt.code === optionValue
            )?.name;

            if (index === 0) {
              const { width: w, height: h } = values;
              if (w && h) {
                name = `${w}x${h}cm`;
              } else {
                if (w) {
                  name = `${w}cm`;
                }
                if (h) {
                  name = `${h}cm`;
                }
              }
            }
            return (
              <Option
                key={options[0]?.code}
                options={options}
                expanded={expanded}
                num={index}
                value={name ?? ""}
                onEditClicked={(num) => {
                  // NOTE: getting rid of all optioins which go after selected
                  // also clearing up form values and touched
                  console.group(`On edit click ${num}`);
                  setExpanded(num);
                  const slicedRawOptionsList = rawOptionsList.slice(0, num + 1);
                  const rawOptionsToClear = rawOptionsList.slice(num);
                  //FIXME: don't use .flat()
                  const valueKeysToUnset = rawOptionsToClear
                    .flat()
                    .map((opt) => [
                      opt.code,
                      ...opt.children.map((opt) => opt.code),
                    ])
                    .flat();
                  valueKeysToUnset.forEach((key) => {
                    setFieldValue(key, undefined);
                    setFieldTouched(key, false);
                  });
                  if (designStep > num) {
                    setFieldValue("designtype", undefined);
                    setFieldTouched("designtype", false);
                  }
                  setCurrentStep(rawOptionsList[num]);
                  onCodesChanged([], null);
                  setRawOptionsList(slicedRawOptionsList);
                  console.log(valueKeysToUnset);
                  console.groupEnd();
                }}
                onNextStep={loadStep}
              />
            );
          })}
        </Form>
      )}
    </>
  );
};

const OptionsWrapper = (props) => {
  return (
    <Formik initialValues={{}}>
      {() => {
        return <Options {...props} />;
      }}
    </Formik>
  );
};

export const LoadedOptions = ({ loadedOptions }) => {
  const metrics = useCanvasMetrics();
  const excludeList = ["height", "width", "amount", "customer-supplied-file"];
  const selectedOptions = loadedOptions.filter(
    (opt) => !excludeList.includes(opt?.code)
  );

  return (
    <div>
      <Accordion expanded>
        <OptionHeader
          editable
          num={0}
          name={t.size}
          value={`${metrics.width}x${metrics.height}cm`}
        />
        <AccordionDetails style={{ padding: "0 16px 0" }}>
          <SizeManager />
        </AccordionDetails>
      </Accordion>
      {selectedOptions.map((option, index) => {
        return (
          <Option
            key={option?.type_code ?? index}
            options={loadedOptions}
            name={option.parent_name}
            num={index + 1}
            value={option?.value ?? option?.name}
            disabled
          />
        );
      })}
    </div>
  );
};

export default OptionsWrapper;
